import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useAppQuery} from '../../../../../../../libs/shared/core/src/services/query/useAppQuery';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {newPalette} from '../../../../../../../libs/shared/ui/src/styles/colorStyles';
import {flexCenter} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../../../helpers/appReduxHelpers';
import {AiReview} from '../../../../../../helpers/models/conversationAiHelpers';
import {
  ConversationAiMarkModel,
  ConversationAiStatusesEnum,
} from '../../../../../../models/api/conversationAiMarkModel';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {tagQueries} from '../../../../../../queries/tagQueries';
import {aiMarkResolveAction} from '../../../../../../store/data/comments/aiSuggestionActions';
import {CommentBodyAiTagSuggestionTagPill} from '../../comment/commentBodyAiTagSuggestionTagPill';
import {AiTagSuggestionBannerButton} from './aiTagSuggestionBannerButton';
import {MessageBanner} from './messageBanner';
import {MessageBannerActionGroup} from './messageBannerActionGroup';
import {MessageBannerTitle} from './messageBannerTitle';

/*
 * Props.
 */

interface AiTagSuggestionBannerProps {
  panelId: ViewerPanelId | undefined;
  conversationId: ConversationModel['id'];
  aiMark: ConversationAiMarkModel;

  isRounded: boolean;
}

/*
 * Styles.
 */

const StyledMessageBanner = styled(MessageBanner)`
  padding-right: 8px;
`;

const StyledMessageBannerTitle = styled(MessageBannerTitle)`
  ${flexCenter};
  gap: 4px;
`;

const StyledCommentBodyAiTagSuggestionTagPill = styled(CommentBodyAiTagSuggestionTagPill)`
  flex-shrink: 0;
  margin: 10px 0;
`;

const StyledButtonsWrapperDiv = styled.div`
  ${flexCenter}
  gap: 8px;
`;

/*
 * Component.
 */

export const AiTagSuggestionBanner: FC<AiTagSuggestionBannerProps> = (props) => {
  const {panelId, conversationId, aiMark} = props;
  const {status: aiStatus, resourceId: tagId} = aiMark;

  const theme = useUserTheme();

  // When reviewing AI tags, we're using a different color scheme to put more emphasis on the suggestions.
  const backgroundColor = theme.greys.shade80;
  const color = theme.invertedText;

  const {data: suggestedTag} = useAppQuery(tagQueries.id(tagId));

  const dispatch = useAppDispatch();
  const onAiReview = useCallback(
    (aiReview: AiReview) => {
      dispatch(aiMarkResolveAction(panelId, conversationId, aiMark, aiReview));
    },
    [aiMark, conversationId, dispatch, panelId],
  );

  return (
    <StyledMessageBanner
      color={backgroundColor}
      iconName="magicWandSmall"
      iconColor={color}
      isRounded={props.isRounded}
    >
      <StyledMessageBannerTitle color={color}>
        <TooltipCoordinator render={() => renderBannerTitle()}>
          <TooltipOverflowCondition>{renderBannerTitle()}</TooltipOverflowCondition>
        </TooltipCoordinator>
        {suggestedTag && <StyledCommentBodyAiTagSuggestionTagPill tag={suggestedTag} />}
      </StyledMessageBannerTitle>
      <MessageBannerActionGroup>{renderBannerActions(aiStatus, onAiReview)}</MessageBannerActionGroup>
    </StyledMessageBanner>
  );
};

/*
 * Helpers.
 */

function renderBannerTitle() {
  return <Trans id="ai-review-tag-suggestion-banner-title">Suggested tag based on message content</Trans>;
}

function renderBannerActions(
  aiStatus: ConversationAiStatusesEnum | undefined,
  onAiReview: (review: AiReview) => void,
) {
  switch (aiStatus) {
    // This case could not happen when reviewing AI tags.
    case ConversationAiStatusesEnum.AUTO_APPROVED:
      return (
        <AiTagSuggestionBannerButton leftIcon="checkmarkCircleSmall" isDisabled>
          <Trans id="ai-tag-suggestion-auto-approved">Added by AI</Trans>
        </AiTagSuggestionBannerButton>
      );

    case ConversationAiStatusesEnum.APPROVED:
      return (
        <AiTagSuggestionBannerButton leftIcon="checkmarkCircleSmall" isDisabled>
          <Trans id="ai-tag-suggestion-approved-review">Approved</Trans>
        </AiTagSuggestionBannerButton>
      );

    case ConversationAiStatusesEnum.DISCARDED:
      return (
        <AiTagSuggestionBannerButton
          // Display an icon only when reviewing AI tags.
          leftIcon="crossCircleSmall"
          isDisabled
        >
          <Trans id="ai-tag-suggestion-discarded-review">Discarded</Trans>
        </AiTagSuggestionBannerButton>
      );

    case ConversationAiStatusesEnum.SUGGESTED:
      return (
        <StyledButtonsWrapperDiv>
          <AiTagSuggestionBannerButton
            leftIcon="checkmarkCircleSmall"
            leftIconColor={newPalette.green.lowlight}
            onClick={() =>
              onAiReview({
                status: ConversationAiStatusesEnum.APPROVED,
                resourceId: undefined,
              })
            }
          >
            <Trans id="ai-tag-suggestion-approve-review">Approve</Trans>
          </AiTagSuggestionBannerButton>

          <AiTagSuggestionBannerButton
            // Display an icon only when reviewing AI tags.
            leftIcon="crossCircleSmall"
            leftIconColor={newPalette.red.lowlight}
            onClick={() =>
              onAiReview({
                status: ConversationAiStatusesEnum.DISCARDED,
                resourceId: undefined,
              })
            }
          >
            <Trans id="ai-tag-suggestion-discard-review">Discard</Trans>
          </AiTagSuggestionBannerButton>
        </StyledButtonsWrapperDiv>
      );

    default:
      return null;
  }
}
